import type { Device, DeviceStatus } from '@/types/device';
import { statusConfig } from '@/types/device';
import { downloadCsv, escapeCsvField, escapeCsvImei } from '@/pages/sysadmin/deviceQueueCsv';
import i18n from '@/i18n/config';
import { uiLanguage } from '@/utils/uiLocale';

function deviceCsvHeaders(language: string): string[] {
  const lng = uiLanguage(language);
  return [
    'IMEI',
    'ICCID',
    'Status',
    i18n.t('copy.admin.deviceCsvOrganization', { lng }),
    i18n.t('copy.admin.deviceCsvRegistration', { lng }),
    i18n.t('copy.admin.deviceCsvMake', { lng }),
    i18n.t('copy.admin.deviceCsvModel', { lng }),
    i18n.t('copy.admin.deviceCsvLastVin', { lng }),
    i18n.t('copy.admin.deviceCsvSimStatus', { lng }),
    i18n.t('copy.admin.deviceCsvCreated', { lng }),
    i18n.t('copy.admin.deviceCsvUpdated', { lng }),
  ];
}

function statusLabel(status: DeviceStatus, language: string): string {
  const cfg = statusConfig[status];
  if (!cfg) return status;
  return language === 'sv' ? cfg.label : cfg.labelEn;
}

function csvDate(value: string | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toISOString().slice(0, 19).replace('T', ' ');
}

export function buildDeviceManagementCsv(devices: Device[], language: string): string {
  const headers = deviceCsvHeaders(language);
  const headerLine = headers.map((h) => escapeCsvField(h)).join(';');
  const dataLines = devices.map((d) =>
    [
      escapeCsvImei(d.imei ?? ''),
      escapeCsvField(d.iccid ?? ''),
      escapeCsvField(statusLabel(d.status, language)),
      escapeCsvField(d.organization_name ?? ''),
      escapeCsvField(d.vehicle_registration ?? ''),
      escapeCsvField(d.vehicle_brand ?? ''),
      escapeCsvField(d.vehicle_model ?? ''),
      escapeCsvField(d.last_known_vin ?? ''),
      escapeCsvField(d.sim_status ?? ''),
      escapeCsvField(csvDate(d.created_at)),
      escapeCsvField(csvDate(d.updated_at)),
    ].join(';'),
  );
  return [headerLine, ...dataLines].join('\r\n');
}

export function deviceManagementCsvFilename(
  statusFilter: DeviceStatus | '',
  language: string,
  now = new Date(),
): string {
  const stamp = now.toISOString().slice(0, 10);
  const statusPart = statusFilter
    ? statusLabel(statusFilter, language).toLowerCase().replace(/\s+/g, '-')
    : i18n.t('common.all', { lng: uiLanguage(language) }).toLowerCase();
  return `enheter-${statusPart}-${stamp}.csv`;
}

export function downloadDeviceManagementCsv(
  devices: Device[],
  statusFilter: DeviceStatus | '',
  language: string,
): void {
  const csv = buildDeviceManagementCsv(devices, language);
  downloadCsv(deviceManagementCsvFilename(statusFilter, language), csv);
}
